<script setup lang="ts" generic="T extends 'file' | 'data'">
import type {
  DeletableFileFieldEmits,
  FieldEmits,
  FieldProps,
  PreviewableFieldFileFieldEmits,
  PreviewableFieldProps,
} from '@/modules/Project/Fields/field-types'
import { provideFieldContext } from '@/modules/Project/Fields/fieldContext'
import type { Field } from '@/modules/Project/Fields/types'
import { canPreviewFileField } from '@/modules/Project/Fields/utils/file'
import FileFieldUpload from '@/modules/Project/FileFieldUpload.vue'
import { useFileField } from '@/modules/Project/useFileField'
import { fileURL } from '@/modules/Project/useProjectStore'
import SidebarFieldActions from '@/sharedComponents/SidebarFieldActions.vue'
import { useDropzone } from '@/sharedComposables/useDropzone'
import BadgeItem from '@/uiKit/BadgeItem.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import { computed, useTemplateRef } from 'vue'

const props = withDefaults(defineProps<FieldProps<T> & PreviewableFieldProps>(), {
  previewable: true,
})

const emit = defineEmits<FieldEmits<T> & DeletableFileFieldEmits & PreviewableFieldFileFieldEmits>()

provideFieldContext({ field: props.field, property: props.property, column: 0, row: 0 })

const { filename, dialogFiles, downloadUrl, onPaste, supportedMimeTypes } = useFileField(
  computed(() => props.field),
)

const dropzone = useTemplateRef('dropzone')
const { isOverDropZone } = useDropzone(dropzone, {
  onAccepted: (files) => {
    dialogFiles.value = files
  },
  multiple: false,
  supportedMimeTypes,
})

defineOptions({ inheritAttrs: false })

const isDeletable = computed(
  () => filename.value && !!(props.field as Field<'file'> | Field<'data'>).manualValue,
)
</script>

<template>
  <FileFieldUpload
    v-slot="{ getTriggerProps, menu }"
    library-picker-confirm-label="Upload file"
    :positioning="{ placement: 'bottom-start', offset: { mainAxis: 2, crossAxis: 1 } }"
    @import:computer="dialogFiles = $event"
  >
    <button
      ref="dropzone"
      class="group flex min-h-7 w-full grow items-center justify-between rounded-corner-8 pl-1 hover:bg-background-transparent-hovered focus:bg-background-transparent-hovered focus:outline-hidden"
      :class="[(menu?.open || isOverDropZone) && 'bg-background-transparent-hovered']"
      v-bind="{ ...getTriggerProps(), ...$attrs }"
      @paste="onPaste"
    >
      <BadgeItem
        v-if="filename"
        variant="neutral"
        size="sm"
        leading-icon="attachment"
        :label="filename"
      />

      <span
        v-if="!filename && !menu?.open"
        class="pl-1 text-text-subtle group-hover:hidden group-focus:hidden"
        >No file</span
      >

      <BadgeItem
        v-if="!filename"
        variant="neutral"
        size="sm"
        class="group-hover:opacity-100 group-focus:opacity-100"
        :class="menu?.open || isOverDropZone ? 'opacity-100' : 'opacity-0'"
        leading-icon="plus"
        label="Add file or drag and drop"
      />
      <SidebarFieldActions>
        <DarwinButton
          v-if="isDeletable"
          size="sm"
          variant="transparent"
          @click.stop="emit('file:delete', field)"
        >
          Delete
        </DarwinButton>

        <DarwinButton
          v-if="previewable && canPreviewFileField(field)"
          size="sm"
          variant="transparent"
          @click.stop="$emit('file:preview', field)"
        >
          Preview
        </DarwinButton>
        <DarwinButton
          v-else-if="downloadUrl"
          size="sm"
          variant="transparent"
          :href="fileURL(field, 'download') || undefined"
          target="_blank"
          @click.stop
        >
          Download
        </DarwinButton>
      </SidebarFieldActions>
    </button>
  </FileFieldUpload>
</template>
